/**
 * ClickReel SDK - Product Card Slide-Up Component
 *
 * Animated product card that slides up from the bottom of the video
 * when video playback reaches a configurable percentage.
 */

import { ProductCardConfig } from './types';

export class ProductCard {
  private element: HTMLElement | null = null;
  private shown = false;
  private config: ProductCardConfig;
  private primaryColor: string;

  constructor(config: ProductCardConfig, primaryColor?: string) {
    this.config = config;
    this.primaryColor = primaryColor || '#6366f1';
  }

  render(container: HTMLElement): void {
    this.element = document.createElement('div');
    this.element.className = 'clickreel-product-card';

    const inner = document.createElement('div');
    inner.className = 'clickreel-product-card-inner';

    // Product info row
    const row = document.createElement('div');
    row.className = 'clickreel-product-card-row';

    if (this.config.productImageUrl) {
      const img = document.createElement('img');
      img.className = 'clickreel-product-card-img';
      img.src = this.config.productImageUrl;
      img.alt = this.config.productName || 'Product';
      row.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'clickreel-product-card-info';

    const name = document.createElement('div');
    name.className = 'clickreel-product-card-name';
    name.textContent = this.config.productName || 'Product';
    info.appendChild(name);

    const priceRow = document.createElement('div');
    if (this.config.productPrice) {
      const price = document.createElement('span');
      price.className = 'clickreel-product-card-price';
      price.textContent = this.config.productPrice;
      priceRow.appendChild(price);
    }
    if (this.config.badgeText) {
      const badge = document.createElement('span');
      badge.className = 'clickreel-product-card-badge';
      badge.textContent = this.config.badgeText;
      priceRow.appendChild(badge);
    }
    info.appendChild(priceRow);
    row.appendChild(info);
    inner.appendChild(row);

    // CTA button
    if (this.config.ctaText) {
      const cta = document.createElement('a');
      cta.className = 'clickreel-product-card-cta';
      cta.href = this.config.ctaUrl || '#';
      cta.target = '_blank';
      cta.rel = 'noopener noreferrer';
      cta.textContent = this.config.ctaText;
      cta.style.background = this.primaryColor;
      inner.appendChild(cta);
    }

    this.element.appendChild(inner);
    container.appendChild(this.element);
  }

  show(): void {
    if (this.shown || !this.element) return;
    this.shown = true;
    requestAnimationFrame(() => {
      this.element?.classList.add('visible');
    });
  }

  hide(): void {
    if (!this.element) return;
    this.element.classList.remove('visible');
  }

  destroy(): void {
    this.element?.remove();
    this.element = null;
    this.shown = false;
  }

  get triggerPercent(): number {
    return this.config.triggerAtPercent ?? 50;
  }

  get hasShown(): boolean {
    return this.shown;
  }
}
